/**
 * TickerTape — port of app/callbacks/misc.update_tape + TAPE_SYMS list.
 * Renders the top strip with global market context. Refreshes every 60s
 * by hitting /api/quote/{symbol} for each ticker in parallel.
 */
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { Quote } from '@/types';
import { GRN, RED, MUT, FONT, ORG } from '@/theme';

// Tape symbols — all US-tradeable so Alpaca's /v2/stocks/* endpoints
// resolve them. The Python v3 used Yahoo-format index/forex/crypto
// tickers (^VIX, EURUSD=X, BTC-USD) but Alpaca's market-data API only
// covers equities + crypto-in-slash-format, so we use ETF proxies
// instead. Labels stay close to the original.
//
//   ^VIX     → VIXY  (volatility ETF, tracks short-term VIX futures)
//   ^TNX     → TLT   (20Y+ Treasury ETF — closest tradeable to 10Y%)
//   EURUSD=X → UUP   (Dollar Index ETF — inverse-ish of EUR/USD)
//   BTC-USD  → IBIT  (BlackRock Bitcoin Trust)
//   ETH-USD  → ETHA  (iShares Ethereum Trust)
const TAPE: Array<[string, string]> = [
  ['SPY',  'S&P'],
  ['QQQ',  'NDX'],
  ['IWM',  'RUT'],
  ['VIXY', 'VIX'],
  ['GLD',  'GOLD'],
  ['USO',  'OIL'],
  ['IBIT', 'BTC'],
  ['ETHA', 'ETH'],
  // DXY (UUP) + 20Y (TLT) dropped: the fixed ProductBar overlay was covering
  // the last tape items, and trimming two low-priority proxies clears ETH
  // without shrinking the (already small) tape font.
];

function TapeItem({ sym, label }: { sym: string; label: string }) {
  // Don't retry on 404 — those mean "Alpaca doesn't cover this symbol"
  // and pounding the endpoint won't change that.
  const { data, isError } = useQuery<Quote>({
    queryKey: ['tape-quote', sym],
    queryFn:  () => api(`/api/quote/${encodeURIComponent(sym)}`),
    refetchInterval: 60_000,
    staleTime:       60_000,
    retry: false,
  });
  const hasData = !isError && data && Number.isFinite(data.last ?? data.mid);
  const px  = hasData ? (data!.last ?? data!.mid) : null;
  // Same as Watchlist: a missing change field must not render as "▲ 0.00%" in
  // green. null already renders as an uncoloured em-dash.
  const pct = hasData && Number.isFinite(data!.change_pct as number) ? (data!.change_pct as number) : null;
  const clr = pct === null ? MUT : pct >= 0 ? GRN : RED;
  const arrow = pct === null ? '—' : pct >= 0 ? '▲' : '▼';

  return (
    <div className="tape-item">
      <span className="tape-sym">{label}</span>
      <span className="tape-px" style={{ color: hasData ? '#e0e0e0' : MUT }}>
        {px !== null ? `$${px.toFixed(2)}` : '—'}
      </span>
      <span className="tape-chg" style={{ color: clr }}>
        {pct !== null ? `${arrow} ${Math.abs(pct).toFixed(2)}%` : '—'}
      </span>
    </div>
  );
}

export function TickerTape() {
  return (
    <div className="tape-bar">
      <div className="tape-item" style={{ borderRight: 'none' }}>
        <span style={{
          color: ORG, fontWeight: 'bold', fontFamily: FONT,
          fontSize: 11, letterSpacing: 2,
        }}>OMNISTRAT</span>
        <span style={{ color: MUT, fontFamily: FONT, fontSize: 8, letterSpacing: 2 }}>v4</span>
      </div>
      {TAPE.map(([sym, label]) => <TapeItem key={sym} sym={sym} label={label} />)}
    </div>
  );
}
